import React from "react";

export type BadgeTone = "green" | "red" | "blue" | "yellow" | "gray";

const toneClasses: Record<BadgeTone, string> = {
  green: "bg-green-100 text-green-800",
  red: "bg-red-100 text-red-800",
  blue: "bg-blue-100 text-blue-800",
  yellow: "bg-yellow-100 text-yellow-800",
  gray: "bg-gray-100 text-gray-800",
};

interface BadgeProps {
  tone: BadgeTone;
  children: React.ReactNode;
  className?: string;
}

/** Small pill badge, e.g. for calibration/result status. */
export const Badge: React.FC<BadgeProps> = ({ tone, children, className = "" }) => (
  <span className={`status-badge ${toneClasses[tone]} ${className}`}>{children}</span>
);

/**
 * Maps a backend calibration/result status string to a Badge. Only the statuses
 * the backend actually emits are recognized (passed/failed/approved/rejected/
 * approve_deviation); anything else renders nothing.
 */
export const getStatusBadge = (status?: string | null): React.ReactElement | null => {
  if (!status) return null;
  const statusLower = status.toLowerCase().trim();

  switch (statusLower) {
    case "passed":
      return <Badge tone="green">Pass</Badge>;
    case "failed":
      return <Badge tone="red">Fail</Badge>;
    case "approved":
      return <Badge tone="blue">Approved</Badge>;
    case "rejected":
      return <Badge tone="red">Rejected</Badge>;
    case "approve_deviation":
      return <Badge tone="yellow">Approved Under Deviation</Badge>;
    default:
      return null;
  }
};
